// Why an agent wouldn't start — the branch BEFORE provider-remedy.ts.
//
// The bug this closes: "Start chat" funnelled EVERY spawn rejection through
// providerRemedy(), a mapper whose whole domain is "a connected provider is
// unusable". A pure AUTHORIZATION refusal from the relay carries no reasonKind,
// so it landed on that mapper's `unknown` branch and told the user:
//
//   "<machine> reported a problem with Claude we couldn't identify. Checking
//    again usually settles it — otherwise sign in again, or use an API key."
//
// Nothing about their Claude login was wrong. The relay had rejected a
// cross-tenant daemon_forward. Signing in again, an API key and "Check again"
// were all incapable of fixing it, and the real reason died in the console.
//
// So: classify FIRST, map SECOND. Only a failure we have positive evidence is a
// provider/backend gap reaches providerRemedy; everything else gets copy that
// matches what actually happened, or — when we genuinely don't know — says so
// and shows the server's own words instead of inventing a cause.
//
// Pure module (no Svelte, no I/O), same contract as provider-remedy.ts and
// install-failure.ts: the consumer owns the toast.

// ─── The relay's denial vocabulary ──────────────────────────────────
//
// MIRROR of the reasons produced by classifyDenialReason in
// packages/server/src/server/cyborg/relay-standalone.ts (DENIAL_REASON_PATTERNS).
// That function is the ONE classifier — prose → reason — and the relay now ships
// its answer to us as the `cyborg:error` envelope's `code`. What lives here is
// only the other half: reason → user copy. Do NOT re-derive a reason from prose
// on this side; a second pattern table would drift the moment the relay reworded
// a gate. Kept in lockstep the same way daemon-scopes.ts / page-access.ts are
// (packages/ui cannot import packages/server).
export const DENIAL_REASONS = [
  "forbidden",
  "not_a_member",
  "permission_denied",
  "not_authorized",
  "no_daemon_access",
  "owner_or_admin_only",
  "admin_required",
  "viewer_role",
  "capability_denied",
  "ownership_only",
  "not_visible",
  "cross_workspace",
] as const;

export type DenialReason = (typeof DENIAL_REASONS)[number];

const DENIAL_REASON_SET: ReadonlySet<string> = new Set(DENIAL_REASONS);

export function isDenialReason(code: string | null | undefined): code is DenialReason {
  return !!code && DENIAL_REASON_SET.has(code);
}

// The relay's one stable NON-denial code (relay-standalone.ts's daemon-forward
// branch with nothing to forward to). Not an authorization failure — there is
// simply no machine online — and its server prose names an internal daemon id,
// so we answer it with our own sentence.
const NO_MACHINE_CODE = "no_daemon";

// ─── Never render an internal id ────────────────────────────────────
//
// Some relay denials interpolate an id for diagnosability — "That daemon is not
// part of this workspace (90bf1a01-…)", "The daemon for this command
// (srv_5IJmGPSjMR9V) isn't connected". That is right for a log and meaningless
// to a person, so the parenthesised id is dropped before any server prose
// reaches the DOM. Matches a uuid or a prefixed opaque id, and only inside
// parentheses, so real prose in brackets survives.
const PARENTHESISED_ID =
  /\s*\((?:[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|[a-z]{2,5}_[A-Za-z0-9_-]{8,})\)/gi;

export function redactInternalIds(message: string): string {
  return message.replace(PARENTHESISED_ID, "").trim();
}

// ─── The taxonomy ───────────────────────────────────────────────────

export interface AgentStartFailureCopy {
  title: string;
  body: string;
}

export type AgentStartFailure =
  // A real provider/backend gap — the caller keeps today's providerRemedy path
  // untouched, including its remedy actions.
  | { kind: "provider" }
  // An authorization refusal. NEVER offers sign-in / API-key / re-check: none of
  // them can grant access, and offering them is what made this screen a lie.
  | ({ kind: "denied"; reason: DenialReason } & AgentStartFailureCopy)
  // Nothing to run on right now.
  | ({ kind: "no_machine" } & AgentStartFailureCopy)
  // We do not know. Say that, and show what the server said.
  | ({ kind: "unknown" } & AgentStartFailureCopy);

export interface AgentStartFailureInput {
  /** What the spawn threw. Read for `.message` / `.code` only. */
  error: unknown;
  /**
   * True when `error` is the daemon's typed ProviderUnavailableError — the spawn
   * gate's own refusal, which is provider evidence on its own even when it
   * carried a backend but no reasonKind. The caller does the `instanceof` so this
   * module stays free of the WS client.
   */
  providerClassified: boolean;
  /** The classified provider reason, from the typed error or the gating provider row. */
  reasonKind: string | null;
  /** Display name of the agent we tried to start. */
  agentName: string;
  /**
   * Display name of the machine the start actually targeted, already resolved
   * (daemonDisplayName). null when we cannot attribute it — we then say "that
   * computer" rather than naming the wrong machine or printing an id.
   */
  machineName: string | null;
}

function readErrorField(error: unknown, field: "message" | "code"): string | null {
  if (typeof error !== "object" || error === null) return null;
  const value = (error as Record<string, unknown>)[field];
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

// One authorization refusal → what is actually wrong and who can undo it.
// `default:` is deliberately absent: DenialReason is closed, so adding a reason
// to the relay's table without writing copy for it fails the build here.
//
// The agent name is never sentence-initial in this copy, so the one fallback
// ("this agent", for a roster row that somehow has no name) always reads.
function denialCopy(
  reason: DenialReason,
  agentName: string,
  machineName: string | null,
): AgentStartFailureCopy {
  const agent = agentName.trim() || "this agent";
  const machine = machineName?.trim() || "that computer";
  switch (reason) {
    case "cross_workspace":
      return {
        title: `Can't start ${agent} from this workspace`,
        body: `The computer ${agent} is set up on doesn't belong to this workspace, so it can't be started from here. Switch to the workspace that owns it, or edit the agent to move it to a computer in this one.`,
      };

    case "no_daemon_access":
      return {
        title: `You don't have access to ${machine}`,
        body: `Running ${agent} on ${machine} needs access to that computer. Ask whoever owns it to give you access, then try again.`,
      };

    case "viewer_role":
      return {
        title: "Viewers can't start agents",
        body: `Your role in this workspace is viewer, which can read along but not run agents. Ask a workspace owner or admin to make you a member.`,
      };

    case "not_a_member":
      return {
        title: "You're not a member of this workspace anymore",
        body: `Your membership here ended, so agents in it can't be started. Reload the page — if it keeps happening, ask a workspace owner or admin to add you back.`,
      };

    // Everything else the relay's table can answer with is a role / ownership /
    // capability gate. They differ server-side but land on the same user
    // sentence: the workspace said no, and only an admin can change that.
    case "forbidden":
    case "permission_denied":
    case "not_authorized":
    case "owner_or_admin_only":
    case "admin_required":
    case "capability_denied":
    case "ownership_only":
    case "not_visible":
      return {
        title: `You're not allowed to start ${agent}`,
        body: `This workspace didn't allow it. Ask a workspace owner or admin if you think you should be able to.`,
      };
  }
}

// Decide what a failed start actually was.
//
// Order matters, and it is evidence-first:
//   1. the daemon's own typed spawn refusal — the only source that KNOWS the
//      provider is the problem;
//   2. the relay's classified authorization denial — the server explicitly said
//      "not allowed", which beats a provider row we merely happen to be holding
//      (providerState.list is a flat global that can even be another machine's
//      catalog, so a stale reasonKind is not evidence about THIS failure);
//   3. a provider row's reasonKind — unchanged from today;
//   4. nothing → honest.
export function classifyAgentStartFailure(input: AgentStartFailureInput): AgentStartFailure {
  if (input.providerClassified) return { kind: "provider" };

  const code = readErrorField(input.error, "code");
  if (isDenialReason(code)) {
    return {
      kind: "denied",
      reason: code,
      ...denialCopy(code, input.agentName, input.machineName),
    };
  }

  const agent = input.agentName.trim() || "this agent";
  if (code === NO_MACHINE_CODE) {
    return {
      kind: "no_machine",
      title: `No computer is online to run ${agent}`,
      body: `No computer in this workspace is connected right now. Open Cyborg on the machine you run agents from, then try again.`,
    };
  }

  if (input.reasonKind) return { kind: "provider" };

  // Genuinely unknown. The one thing we must NOT do here is name a cause — the
  // whole defect was asserting "Claude" when nobody had checked Claude. Show what
  // the server said (minus internal ids) and nothing else.
  const message = readErrorField(input.error, "message");
  return {
    kind: "unknown",
    title: `Couldn't start ${agent}`,
    body: (message ? redactInternalIds(message) : "") || "The computer it runs on didn't say why.",
  };
}
